import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import { MethodBadge } from './MethodBadge';
import { Loader2, RefreshCw, Trash2, CheckCircle2 } from 'lucide-react';

export const SyncDialog = ({
  open,
  onOpenChange,
  syncPreview,
  syncResult,
  syncLoading,
  onSync,
  onClear,
  onClose
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5 text-blue-600" />
            Sincronizar APIs Internas
          </DialogTitle>
        </DialogHeader>
        
        <ScrollArea className="flex-1">
          {syncResult ? (
            <div className="space-y-4">
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                  <span className="font-medium text-green-800">Sincronización Completada</span>
                </div>
                <div className="grid grid-cols-3 gap-4 text-center mt-4">
                  <div className="p-3 bg-white rounded-lg">
                    <div className="text-2xl font-bold text-green-600">{syncResult.summary?.created || 0}</div>
                    <div className="text-sm text-gray-500">Creadas</div>
                  </div>
                  <div className="p-3 bg-white rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">{syncResult.summary?.updated || 0}</div>
                    <div className="text-sm text-gray-500">Actualizadas</div>
                  </div>
                  <div className="p-3 bg-white rounded-lg">
                    <div className="text-2xl font-bold text-gray-600">{syncResult.summary?.total_routes_found || 0}</div>
                    <div className="text-sm text-gray-500">Total Rutas</div>
                  </div>
                </div>
              </div>
              
              {syncResult.errors?.length > 0 && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm font-medium text-red-800 mb-2">Errores:</p>
                  <ul className="text-sm text-red-600 list-disc list-inside">
                    {syncResult.errors.map((err, i) => (
                      <li key={i}>{err}</li>
                    ))}
                  </ul>
                </div>
              )}
              
              <p className="text-sm text-gray-600">
                Las herramientas sincronizadas están <strong>deshabilitadas por defecto</strong>. 
                Actívalas manualmente para que el agente pueda usarlas.
              </p>
            </div>
          ) : syncPreview ? (
            <div className="space-y-4">
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  Se encontraron <strong>{syncPreview.total_routes}</strong> endpoints en los controladores del sistema.
                  Todas las herramientas se crearán como <strong>inactivas</strong>.
                </p>
              </div>
              
              <div className="space-y-2">
                <h4 className="font-medium">Controladores ({Object.keys(syncPreview.controllers || {}).length})</h4>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(syncPreview.controllers || {}).map(([name, count]) => (
                    <div key={name} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <span className="text-sm font-mono">{name}</span>
                      <Badge variant="secondary">{count} rutas</Badge>
                    </div>
                  ))}
                </div>
              </div>
              
              <Separator />
              
              <div className="space-y-2">
                <h4 className="font-medium">Rutas a Sincronizar</h4>
                <div className="max-h-64 overflow-y-auto space-y-1">
                  {syncPreview.routes?.map((route, i) => (
                    <div key={i} className="flex items-center gap-2 p-2 bg-gray-50 rounded text-sm">
                      <MethodBadge method={route.method} />
                      <span className="font-mono text-xs truncate flex-1">{route.endpoint}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
            </div>
          )}
        </ScrollArea>
        
        <DialogFooter className="border-t pt-4">
          <div className="flex items-center gap-2 w-full justify-between">
            <Button
              variant="outline"
              onClick={onClear}
              disabled={syncLoading}
              className="text-red-600 hover:text-red-700"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Limpiar Sincronizadas
            </Button>
            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose}>
                Cerrar
              </Button>
              {!syncResult && (
                <Button onClick={onSync} disabled={syncLoading}>
                  {syncLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                  Sincronizar Ahora
                </Button>
              )}
            </div>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SyncDialog;
